export const MACHINE_ACCESS_ROLES = {
	TECHNICIAN: 'TECHNICIAN',
	OPERATOR: 'OPERATOR',
	USER: 'USER',
};

const PROFILE_DURATION_DEFAULTS = {
	[MACHINE_ACCESS_ROLES.TECHNICIAN]: {
		machineAccessValidForMinutes: 60,
		recipeOrderTimeoutMinutes: 60,
	},
	[MACHINE_ACCESS_ROLES.OPERATOR]: {
		machineAccessValidForMinutes: 1,
		recipeOrderTimeoutMinutes: 8 * 60,
	},
	[MACHINE_ACCESS_ROLES.USER]: {
		machineAccessValidForMinutes: 1,
		recipeOrderTimeoutMinutes: 1,
	},
};

const durationDefaultsFor = (role) =>
	PROFILE_DURATION_DEFAULTS[role] || PROFILE_DURATION_DEFAULTS[MACHINE_ACCESS_ROLES.TECHNICIAN];

const maximumRecipesDefaultFor = (role) => (role === MACHINE_ACCESS_ROLES.USER ? 3 : 0);

export const DEFAULT_FAILED_PIN_LOCKOUT_MINUTES = 65;

export const MACHINE_ACCESS_DAYS = [
	{ key: 'monday', label: 'Monday' },
	{ key: 'tuesday', label: 'Tuesday' },
	{ key: 'wednesday', label: 'Wednesday' },
	{ key: 'thursday', label: 'Thursday' },
	{ key: 'friday', label: 'Friday' },
	{ key: 'saturday', label: 'Saturday' },
	{ key: 'sunday', label: 'Sunday' },
];

const DEFAULT_SCHEDULE = MACHINE_ACCESS_DAYS.reduce(
	(schedule, day) => ({
		...schedule,
		[day.key]: '',
	}),
	{ enabled: false },
);

let nextClientProfileId = 0;

const createClientProfileId = () => {
	nextClientProfileId += 1;
	return `machine-access-profile-${nextClientProfileId}`;
};

const numberOrDefault = (value, defaultValue) =>
	typeof value === 'number' && !Number.isNaN(value) ? value : defaultValue;

export const createMachineAccessProfile = (role) => {
	const durationDefaults = durationDefaultsFor(role);

	return {
		clientId: createClientProfileId(),
		profileId: null,
		role,
		name: '',
		pin: '',
		newPin: '',
		confirmPin: '',
		machineAccessValidForMinutes: durationDefaults.machineAccessValidForMinutes,
		recipeOrderTimeoutMinutes: durationDefaults.recipeOrderTimeoutMinutes,
		maximumRecipes: maximumRecipesDefaultFor(role),
		recipeIds: [],
	};
};

const normalizeProfile = (profile) => {
	const durationDefaults = durationDefaultsFor(profile.role);
	const configuredMachineAccessMinutes = numberOrDefault(
		profile.machineAccessValidForMinutes,
		durationDefaults.machineAccessValidForMinutes,
	);
	const recipeOrderTimeoutMinutes = numberOrDefault(
		profile.recipeOrderTimeoutMinutes,
		durationDefaults.recipeOrderTimeoutMinutes,
	);

	return {
		clientId: profile.clientId || profile.profileId || createClientProfileId(),
		profileId: profile.profileId || null,
		role: profile.role,
		name: profile.name || '',
		pin: profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN ? '' : profile.pin || '',
		newPin: '',
		confirmPin: '',
		machineAccessValidForMinutes:
			profile.role === MACHINE_ACCESS_ROLES.USER ? recipeOrderTimeoutMinutes : configuredMachineAccessMinutes,
		recipeOrderTimeoutMinutes,
		maximumRecipes: numberOrDefault(profile.maximumRecipes, maximumRecipesDefaultFor(profile.role)),
		recipeIds: Array.isArray(profile.recipeIds) ? [...profile.recipeIds] : [],
	};
};

export const normalizeMachineAccess = (configuration = {}) => {
	const profiles = Array.isArray(configuration.profiles) ? configuration.profiles.map(normalizeProfile) : [];

	if (!profiles.some((profile) => profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN)) {
		profiles.unshift({
			...createMachineAccessProfile(MACHINE_ACCESS_ROLES.TECHNICIAN),
			name: 'Technician',
		});
	}

	return {
		enabled: Boolean(configuration.enabled),
		failedPinLockoutMinutes: numberOrDefault(
			configuration.failedPinLockoutMinutes,
			DEFAULT_FAILED_PIN_LOCKOUT_MINUTES,
		),
		recipeAllowanceResetTime: configuration.recipeAllowanceResetTime || '00:00',
		orderingSchedule: {
			...DEFAULT_SCHEDULE,
			...(configuration.orderingSchedule || {}),
			enabled: Boolean(configuration.orderingSchedule?.enabled),
		},
		profiles,
	};
};

export const getEffectiveProfilePin = (profile) =>
	profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN && profile.newPin ? profile.newPin : profile.pin;

const profileErrorKey = (profile, field) => `${profile.clientId}.${field}`;

export const validateMachineAccess = (configuration) => {
	const errors = {};
	const globalErrors = [];
	const technicians = configuration.profiles.filter(
		(profile) => profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN,
	);

	if (technicians.length !== 1) {
		globalErrors.push('Exactly one technician profile is required.');
	}

	if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(configuration.recipeAllowanceResetTime || '')) {
		errors.recipeAllowanceResetTime = 'Use a valid time in 24-hour HH:mm format.';
	}

	if (!Number.isInteger(configuration.failedPinLockoutMinutes) || configuration.failedPinLockoutMinutes <= 0) {
		errors.failedPinLockoutMinutes = 'Enter a whole number greater than zero.';
	}

	if (configuration.orderingSchedule.enabled) {
		MACHINE_ACCESS_DAYS.forEach(({ key, label }) => {
			const window = configuration.orderingSchedule[key] || '';
			if (window && !/^(?:[01]\d|2[0-3]):[0-5]\d-(?:[01]\d|2[0-3]):[0-5]\d$/.test(window)) {
				errors[`orderingSchedule.${key}`] = `${label} needs both an opening and closing time.`;
			}
		});
	}

	const profilesToSave = configuration.enabled ? configuration.profiles : technicians;
	const pinOwners = {};

	profilesToSave.forEach((profile) => {
		const nameKey = profileErrorKey(profile, 'name');
		const pinKey = profileErrorKey(profile, 'pin');
		const effectivePin = getEffectiveProfilePin(profile);
		const retainsHiddenTechnicianPin =
			profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN &&
			Boolean(profile.profileId) &&
			!profile.pin &&
			!profile.newPin &&
			!profile.confirmPin;

		if (!profile.name || !profile.name.trim()) {
			errors[nameKey] = 'A name is required.';
		}

		if (profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN && (profile.newPin || profile.confirmPin)) {
			if (!/^\d{6}$/.test(profile.newPin || '')) {
				errors[pinKey] = 'The new technician PIN must contain exactly 6 digits.';
			}
			if (profile.newPin !== profile.confirmPin) {
				errors[profileErrorKey(profile, 'confirmPin')] = 'The two technician PIN entries do not match.';
			}
		}

		if (!retainsHiddenTechnicianPin) {
			if (!/^\d{6}$/.test(effectivePin || '')) {
				if (!errors[pinKey]) {
					errors[pinKey] = 'The PIN must contain exactly 6 digits.';
				}
			} else if (pinOwners[effectivePin]) {
				errors[pinKey] = 'This PIN is already used by another profile.';
				errors[profileErrorKey(pinOwners[effectivePin], 'pin')] =
					'This PIN is already used by another profile.';
			} else {
				pinOwners[effectivePin] = profile;
			}
		}

		if (
			profile.role !== MACHINE_ACCESS_ROLES.USER &&
			(!Number.isInteger(profile.machineAccessValidForMinutes) || profile.machineAccessValidForMinutes <= 0)
		) {
			errors[profileErrorKey(profile, 'machineAccessValidForMinutes')] =
				'Enter a whole number greater than zero.';
		}

		if (!Number.isInteger(profile.recipeOrderTimeoutMinutes) || profile.recipeOrderTimeoutMinutes <= 0) {
			errors[profileErrorKey(profile, 'recipeOrderTimeoutMinutes')] =
				'Enter a whole number greater than zero.';
		}

		if (!Number.isInteger(profile.maximumRecipes) || profile.maximumRecipes < 0) {
			errors[profileErrorKey(profile, 'maximumRecipes')] = 'Enter zero for unlimited, or a positive whole number.';
		}
	});

	const messages = [...globalErrors, ...Object.values(errors)];

	return {
		isValid: messages.length === 0,
		errors,
		globalErrors,
		firstMessage: messages[0] || '',
	};
};

const serializeProfile = (profile) => {
	const serializedProfile = {
		profileId: profile.profileId || null,
		role: profile.role,
		name: profile.name.trim(),
		machineAccessValidForMinutes:
			profile.role === MACHINE_ACCESS_ROLES.USER
				? profile.recipeOrderTimeoutMinutes
				: profile.machineAccessValidForMinutes,
		recipeOrderTimeoutMinutes: profile.recipeOrderTimeoutMinutes,
		maximumRecipes: profile.maximumRecipes,
		recipeIds: [...profile.recipeIds],
	};
	const pin = getEffectiveProfilePin(profile);

	if (pin) {
		serializedProfile.pin = pin;
	}

	return serializedProfile;
};

export const serializeMachineAccess = (configuration) => {
	const profiles = configuration.enabled
		? configuration.profiles
		: configuration.profiles.filter((profile) => profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN);

	return {
		enabled: configuration.enabled,
		failedPinLockoutMinutes: configuration.failedPinLockoutMinutes,
		recipeAllowanceResetTime: configuration.recipeAllowanceResetTime,
		orderingSchedule: {
			...configuration.orderingSchedule,
		},
		profiles: profiles.map(serializeProfile),
	};
};

export const machineAccessEquals = (left, right) => {
	if (!left || !right) {
		return left === right;
	}

	const comparableConfiguration = (configuration) => ({
		payload: serializeMachineAccess(configuration),
		technicianPinEntries: configuration.profiles
			.filter((profile) => profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN)
			.map((profile) => ({ newPin: profile.newPin, confirmPin: profile.confirmPin })),
	});

	return JSON.stringify(comparableConfiguration(left)) === JSON.stringify(comparableConfiguration(right));
};

export const copyMachineAccess = (configuration) => JSON.parse(JSON.stringify(configuration));
